import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import { ModalManager } from '../../../../Components/Web/Modal/hooks/useModalManager';
import { MODALPROMPT } from '../../../../Components/Web/Modal/ModalPrompt/config';
import constants from '../../../../Config/constants';
import useAddress from '../../../../Hooks/useAddress';
import useMealPlan from '../../../../Hooks/useMealPlan';
import routeList, { RootRouteProps } from '../../../../Routes/list';
import {
  setAddressSelection,
  setDropOffOptionSelection,
} from '../../../../RTK/checkout';
import { checkoutFlagSelector } from '../../../../RTK/checkout/selectors';
import { checkout } from '../../../../RTK/defaultValues';
import { setMealPlanAddressSelection } from '../../../../RTK/mealPlan';
import { selectMealPlanCheckoutFlag } from '../../../../RTK/mealPlan/selectors';
import {
  addressSelector,
  lastUsedAddressSelector,
} from '../../../../RTK/user/selectors';

import useCheckoutHandler from '../../controller/useCheckoutHandler';

function useDropOffOptions(selectedAddress) {
  const dispatch = useDispatch();
  const { isMealPlan } = useMealPlan();
  const { isDelivery, checkoutInfo, onUpdateCheckoutDetails } =
    useCheckoutHandler();
  const isMounted = useRef<boolean>();
  const route = useRoute();
  const navigation = useNavigation<StackNavigationProp<RootRouteProps>>();
  const { setActiveAddress } = useAddress();
  const isFromAddressCheckout = useSelector(
    (state) => checkoutFlagSelector(state).addressSelection,
  );
  const isFromAddressMealPlanCheckout = useSelector(selectMealPlanCheckoutFlag);
  const userAddresses = useSelector(addressSelector);
  const userLastUsedAddresses = useSelector(lastUsedAddressSelector);

  const [hideAccordion, setHideAccordion] = useState(true);

  const hasLastUsedAddresses = !lodashIsEmpty(userLastUsedAddresses);
  const isNotInLastUsedAddress = !userLastUsedAddresses.includes(
    selectedAddress?.id,
  );
  const displayAddress = selectedAddress;

  // this hooks handle the auto closing of accordion, must not be execute onload
  useEffect(() => {
    if (isMounted.current) {
      // execute only if mounted
      if (isFromAddressCheckout || isFromAddressMealPlanCheckout) {
        // if user select address in saved address, from checkout
        if (userLastUsedAddresses.includes(selectedAddress?.id)) {
          // if selected address is in last used address
          setTimeout(() => {
            setHideAccordion(false); // open accordion
          }, 100); // added delay for the accordion animation propagate (not gonna work without delay)
        }
      }
    }
  }, [
    isFromAddressCheckout, // if user access the address selection on normal checkout
    isFromAddressMealPlanCheckout, // if user access the address selection on meal plan checkout
    selectedAddress?.id, // if selected address changed
    userLastUsedAddresses, // if last used address changed
  ]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      dispatch(setAddressSelection(undefined));
      dispatch(setDropOffOptionSelection(undefined));
      dispatch(setMealPlanAddressSelection(false));
    };
  }, [dispatch]);

  const _getRowData = (addressId: string, index: number) => {
    const addressInfo = lodashFind(userAddresses, { id: addressId });
    return {
      address: addressInfo,
      isSelected: selectedAddress?.id === addressInfo?.id,
      isLast: userLastUsedAddresses.length - 1 === index,
    };
  };

  const _setAddressSelectionFlag = () => {
    if (isMealPlan) {
      dispatch(setMealPlanAddressSelection(true));
    } else {
      dispatch(setAddressSelection(checkoutInfo.store_id));
    }
  };

  const _onAddressPressed = () => {
    _setAddressSelectionFlag();
    if (constants.isWeb) {
      ModalManager.show(MODALPROMPT.listAddress, {
        payload: {
          storeId: checkoutInfo.store_id,
          navigation: navigation,
          route: route,
        },
      });
    } else {
      navigation.navigate(routeList.ADDRESS_SELECTOR);
    }
  };

  const _onLastUsedAddressPressed = (value) => {
    onUpdateCheckoutDetails(checkout.keys.DELIVERY_ADDRESS, value);
  };

  const _onSelectedAddressPressed = async () => {
    _setAddressSelectionFlag();
    const [longitude, latitude] = displayAddress?.geo_json_point.coordinates;
    const result = await ModalManager.show(MODALPROMPT.addressDetails, {
      payload: {
        status: 'edit',
        selectedAddress: {
          ...displayAddress,
          index: displayAddress.id,
          latitude: latitude,
          longitude: longitude,
        },
        navigation: navigation,
        route: route,
      },
    });
    if (result?.active) {
      setActiveAddress(result);
    }
  };

  const _toggleAccordion = () => setHideAccordion((prev) => !prev);

  return {
    displayAddress,
    hasLastUsedAddresses,
    hideAccordion,
    isDelivery,
    isMealPlan,
    isNotInLastUsedAddress,
    userLastUsedAddresses,
    getRowData: _getRowData,
    onAddressPressed: _onAddressPressed,
    onLastUsedAddressPressed: _onLastUsedAddressPressed,
    onSelectedAddressPressed: _onSelectedAddressPressed,
    toggleAccordion: _toggleAccordion,
  };
}

export default useDropOffOptions;
